<template>
  <v-hover v-slot="{ hover }">
    <v-card
      class="ps-metric"
      :class="[
        `ps-metric--${variant}`,
        { 'ps-metric--raised': hover },
        { 'ps-metric--multiline': multiline },
      ]"
      elevation="0"
    >
      <v-card-text class="ps-metric__inner">
        <div class="ps-metric__head">
          <div class="ps-metric__icon-wrap" :class="`ps-metric__icon-wrap--${variant}`">
            <v-icon :color="iconTone">{{ icon }}</v-icon>
          </div>
          <div class="ps-metric__title">
            {{ title }}
            <v-tooltip v-if="tooltip" bottom max-width="280" content-class="premium-banner-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ps-metric__help" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
              </template>
              {{ tooltip }}
            </v-tooltip>
          </div>
        </div>
        <div class="ps-metric__value" :class="{ 'ps-metric__value--sm': multiline }">{{ displayValue }}</div>
      </v-card-text>
    </v-card>
  </v-hover>
</template>

<script lang="ts">
import { defineComponent } from "vue";

type SummaryMetricVariant = "finance" | "network" | "brand" | "amber" | "slate";

const iconColors: Record<SummaryMetricVariant, string> = {
  finance: "#1f8a4d",
  network: "#242968",
  brand: "#c41e3a",
  amber: "#b45309",
  slate: "#475569",
};

export default defineComponent({
  name: "PartnerSummaryMetricCard",

  props: {
    title: { type: String, required: true },
    value: { type: [String, Number], required: true },
    icon: { type: String, required: true },
    variant: {
      type: String,
      default: "network",
      validator: (v: string) => ["finance", "network", "brand", "amber", "slate"].includes(v),
    },
    tooltip: { type: String, default: "" },
    /** Valores longos (ex.: intervalo de datas) */
    multiline: { type: Boolean, default: false },
  },

  computed: {
    displayValue(): string {
      const v = this.value;
      return v === null || v === undefined ? "—" : String(v);
    },
    iconTone(): string {
      return iconColors[this.variant as SummaryMetricVariant] || iconColors.network;
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.ps-metric {
  position: relative;
  border-radius: $br-radius-xl !important;
  border: 1px solid $br-border-soft !important;
  box-shadow: $br-shadow-md !important;
  background: $br-bg-elevated !important;
  transition: transform $br-transition-base, box-shadow $br-transition-base;
  height: 100%;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0 auto auto 0;
    width: 100%;
    height: 3px;
  }

  &::after {
    content: "";
    position: absolute;
    right: -18px;
    top: -18px;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    opacity: 0.45;
    pointer-events: none;
  }
}

.ps-metric--raised {
  transform: translateY(-4px);
  box-shadow: $br-shadow-lg !important;
}

.ps-metric--finance {
  &::before { background: linear-gradient(90deg, $br-success, lighten($br-success, 12%)); }
  &::after { background: rgba(36, 168, 89, 0.12); }
}

.ps-metric--network {
  &::before { background: linear-gradient(90deg, $br-navy, lighten($br-navy, 18%)); }
  &::after { background: rgba(36, 41, 104, 0.1); }
}

.ps-metric--brand {
  &::before { background: linear-gradient(90deg, $br-brand, $br-brand-light); }
  &::after { background: rgba(229, 24, 61, 0.1); }
}

.ps-metric--amber {
  &::before { background: linear-gradient(90deg, $br-warning, lighten($br-warning, 12%)); }
  &::after { background: rgba(217, 119, 6, 0.12); }
}

.ps-metric--slate {
  &::before { background: linear-gradient(90deg, #64748b, #94a3b8); }
  &::after { background: rgba(100, 116, 139, 0.12); }
}

.ps-metric__inner {
  position: relative;
  z-index: 1;
  padding: 16px 18px !important;
}

.ps-metric__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}

.ps-metric__icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
}

.ps-metric__icon-wrap .v-icon {
  font-size: 22px !important;
}

.ps-metric__icon-wrap--finance {
  background: linear-gradient(145deg, rgba(36, 168, 89, 0.22), rgba(36, 168, 89, 0.08));
}

.ps-metric__icon-wrap--network {
  background: linear-gradient(145deg, rgba(36, 41, 104, 0.18), rgba(36, 41, 104, 0.06));
}

.ps-metric__icon-wrap--brand {
  background: linear-gradient(145deg, rgba(229, 24, 61, 0.2), rgba(229, 24, 61, 0.07));
}

.ps-metric__icon-wrap--amber {
  background: linear-gradient(145deg, rgba(217, 119, 6, 0.22), rgba(217, 119, 6, 0.08));
}

.ps-metric__icon-wrap--slate {
  background: linear-gradient(145deg, rgba(100, 116, 139, 0.2), rgba(100, 116, 139, 0.07));
}

.ps-metric__title {
  font-size: $br-font-size-xs;
  font-weight: 700;
  color: $br-muted;
  line-height: 1.35;
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  padding-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.35px;
}

.ps-metric__help {
  color: $br-placeholder !important;
  cursor: help;
  flex-shrink: 0;
}

.ps-metric__value {
  font-size: $br-font-size-2xl;
  font-weight: 800;
  color: $br-ink-soft;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
}

.ps-metric__value--sm {
  font-size: $br-font-size-md;
  font-weight: 700;
  line-height: 1.35;
  word-break: break-word;
  letter-spacing: 0;
}

.ps-metric--multiline .ps-metric__inner {
  padding-bottom: 12px !important;
}
</style>
